import { cloneElement, isValidElement, useEffect, useId, useRef, useState } from 'react'
import { MoreHorizontal } from 'lucide-react'
import { IconButton } from '../primitives/IconButton'
import { Popover } from './Popover'
import styles from './Overlays.module.css'
const focusableItemSelector = '[role="menuitem"]:not(:disabled), [role="menuitemradio"]:not(:disabled)'

// `footer` is a note under the items (say, what they act on). It sits outside
// the menu role, so arrow keys skip it, and describes the menu.
export function Menu({ trigger, items = [], align = 'end', triggerLabel = 'More actions', className = '', variant = 'default', selectionMode, footer = null }) {
  const [open, setOpen] = useState(false)
  const menuRef = useRef(null)
  const menuId = useId()
  const footerId = `${menuId}-footer`
  const visibleItems = items.filter((item) => !item.hidden)
  const rich = variant === 'rich'
  const singleSelection = selectionMode === 'single'

  useEffect(() => {
    if (!open) return undefined
    const timer = window.setTimeout(() => menuRef.current?.querySelector(focusableItemSelector)?.focus(), 0)
    return () => window.clearTimeout(timer)
  }, [open])

  const defaultTrigger = <IconButton label={triggerLabel}><MoreHorizontal size={18} aria-hidden="true" /></IconButton>
  const providedTrigger = trigger || defaultTrigger
  const namedTrigger = isValidElement(providedTrigger)
    ? cloneElement(providedTrigger, {
      'aria-label': providedTrigger.props['aria-label'],
      title: providedTrigger.props.title || triggerLabel,
      'aria-haspopup': 'menu',
    })
    : providedTrigger

  const focusItem = (current, direction) => {
    const itemsInMenu = [...(menuRef.current?.querySelectorAll(focusableItemSelector) || [])]
    const currentIndex = itemsInMenu.indexOf(current)
    itemsInMenu[(currentIndex + direction + itemsInMenu.length) % itemsInMenu.length]?.focus()
  }

  return (
    <Popover trigger={namedTrigger} open={open} onOpenChange={setOpen} placement={`bottom-${align === 'left' || align === 'start' ? 'start' : 'end'}`} label={triggerLabel} contentClassName={`${styles.menu} ${rich ? styles.menuRich : ''}`.trim()} className={className}>
      <div ref={menuRef} role="menu" aria-label={triggerLabel} aria-describedby={footer ? footerId : undefined} className={styles.menuList} onKeyDown={(event) => {
        if (event.key === 'ArrowDown') { event.preventDefault(); focusItem(event.target, 1) }
        if (event.key === 'ArrowUp') { event.preventDefault(); focusItem(event.target, -1) }
        if (event.key === 'Home') { event.preventDefault(); menuRef.current?.querySelector(focusableItemSelector)?.focus() }
        if (event.key === 'End') { event.preventDefault(); [...(menuRef.current?.querySelectorAll(focusableItemSelector) || [])].at(-1)?.focus() }
        // Tab closes the menu; Popover puts focus back on the trigger, so a
        // focus-trapped parent such as a drawer keeps its loop (#863).
        if (event.key === 'Tab') { event.preventDefault(); setOpen(false) }
      }}>
        {visibleItems.map((item) => item.separator ? <hr key={item.key} className={styles.separator} /> : (
          <button
            key={item.key}
            type="button"
            role={singleSelection ? 'menuitemradio' : 'menuitem'}
            aria-checked={singleSelection ? Boolean(item.selected) : undefined}
            aria-labelledby={rich && item.description ? `${menuId}-${item.key}-label` : undefined}
            aria-describedby={rich && item.description ? `${menuId}-${item.key}-description` : undefined}
            className={[
              styles.menuItem,
              rich ? styles.menuItemRich : '',
              item.selected ? styles.menuItemSelected : '',
              item.danger ? styles.menuItemDanger : '',
            ].filter(Boolean).join(' ')}
            disabled={item.disabled}
            onClick={() => {
              if (item.disabled) return
              setOpen(false)
              ;(item.onSelect || item.onClick)?.()
            }}
          >
            {rich ? (
              <>
                {item.icon ? <span className={styles.menuItemIcon}>{isValidElement(item.icon) ? item.icon : <item.icon size={16} aria-hidden="true" />}</span> : null}
                <span className={styles.menuItemBody}>
                  <span id={item.description ? `${menuId}-${item.key}-label` : undefined} className={styles.menuItemLabel}>{item.label}</span>
                  {item.description ? <span id={`${menuId}-${item.key}-description`} className={styles.menuItemDescription}>{item.description}</span> : null}
                </span>
              </>
            ) : (
              <>
                {item.icon ? (isValidElement(item.icon) ? item.icon : <item.icon size={16} aria-hidden="true" />) : null}
                {item.label}
              </>
            )}
          </button>
        ))}
      </div>
      {footer ? <div id={footerId} className={styles.menuFooter}>{footer}</div> : null}
    </Popover>
  )
}
